import {
  coachSubFunctionLabel,
  isCoachSubFunctionSlug,
  ZERO,
} from '@/constants';
import { NotificationOptions } from '@/types';
import { Button, Chip, Icon, Typography } from '@/ui';
import { formatPhoneNumber } from '@/utils';

import type { User } from '@/types';
import type { FC } from 'react';

interface CoachDetailsProps {
  coach: User;
  onEditAccount?: () => void;
  onEditAdmin?: () => void;
  showSubFunctions?: boolean;
}

interface CoachSubFunctionsDetailsProps {
  coach: User;
  show: boolean;
}

const CoachSubFunctionsDetails: FC<CoachSubFunctionsDetailsProps> = ({
  coach,
  show,
}) => {
  if (!show) return null;

  const reviewedSubFunctions = (coach.coachProfile?.subFunctions ?? []).filter(
    isCoachSubFunctionSlug,
  );

  return (
    <div className="space-y-2">
      <Typography
        className="flex items-center gap-2 text-black"
        variant="subtitle2"
      >
        <Icon className="text-sage-600">sell</Icon>
        Sub-functions
      </Typography>
      {reviewedSubFunctions.length ? (
        <div className="flex min-w-0 flex-wrap gap-2">
          {reviewedSubFunctions.map((subFunction) => (
            <Chip
              key={subFunction}
              className="max-w-full [&_.MuiChip-label]:truncate"
              color="info"
              label={coachSubFunctionLabel(subFunction)}
              size="small"
            />
          ))}
        </div>
      ) : (
        <Typography className="text-sage-500" variant="caption">
          No sub-functions selected.
        </Typography>
      )}
    </div>
  );
};

const CoachDetails: FC<CoachDetailsProps> = ({
  coach,
  onEditAccount = undefined,
  onEditAdmin = undefined,
  showSubFunctions = false,
}) => {
  const notifyEmail =
    coach.userNotifications?.[NotificationOptions.Email] ?? false;
  const notifySms = coach.userNotifications?.[NotificationOptions.Sms] ?? false;

  let notifications = 'Off';
  if (notifyEmail && !notifySms) {
    notifications = 'Email';
  } else if (notifySms && notifyEmail) {
    notifications = 'SMS & Email';
  } else if (notifySms) {
    notifications = 'SMS';
  }

  const title = onEditAdmin ? 'Admin Settings' : 'Account Details';
  const showAdminDetails = !!onEditAdmin;
  const showAccountDetails = !!onEditAccount || showAdminDetails;
  const formatSeatLabel = (seat: string) =>
    seat === 'standard_leadership'
      ? 'Standard/Leadership'
      : seat.charAt(0).toUpperCase() + seat.slice(1);
  const seatRateEntries = (
    Object.entries(coach.coachProfile?.seatTypeRates || {}) as [
      string,
      number | null,
    ][]
  ).filter(([, v]) => v !== null) as [string, number][];

  return (
    <section className="space-y-4">
      <div className="mb-4 flex items-center justify-between">
        <Typography variant="subtitle1">{title}</Typography>
        {!!onEditAccount && (
          <Button
            color="primary"
            size="small"
            variant="contained"
            onClick={onEditAccount}
          >
            Manage
          </Button>
        )}
        {!!onEditAdmin && (
          <Button
            color="error"
            size="small"
            variant="contained"
            onClick={onEditAdmin}
          >
            Manage
          </Button>
        )}
      </div>
      <Typography
        className="flex items-center gap-2 text-sage-600"
        variant="subtitle2"
      >
        <Icon className="text-sage-600">sports</Icon>
        Account status: {coach.isActive ? 'Enabled' : 'Disabled'}
      </Typography>
      <Typography
        className="flex items-center gap-2 text-sage-600"
        variant="subtitle2"
      >
        <Icon className="text-sage-600">mail</Icon>
        {coach.email}
      </Typography>
      {!!coach.profile?.phone && (
        <Typography
          className="flex items-center gap-2 text-sage-600"
          variant="subtitle2"
        >
          <Icon className="text-sage-600">call</Icon>
          {formatPhoneNumber(coach.profile.phone)}
        </Typography>
      )}
      <Typography
        className="flex items-center gap-2 text-sage-600"
        variant="subtitle2"
      >
        <Icon className="text-sage-600">notifications</Icon>
        {notifications}
      </Typography>
      {showAccountDetails && (
        <Typography
          className="flex items-center gap-2 text-sage-600"
          variant="subtitle2"
        >
          <Icon className="text-sage-600">account_circle</Icon>
          Members: {(coach as { coachees?: User[] }).coachees?.length ?? ZERO}
        </Typography>
      )}
      {showAccountDetails && (
        <Typography
          className="flex items-center gap-2 text-sage-600"
          variant="subtitle2"
        >
          <Icon className="text-sage-600">communities</Icon>
          Capacity: {coach.coachProfile?.capacity ?? ZERO}
        </Typography>
      )}
      <CoachSubFunctionsDetails coach={coach} show={showSubFunctions} />
      {showAdminDetails && !!coach.coachProfile?.startDate && (
        <Typography
          className="flex items-center gap-2 text-sage-600"
          variant="subtitle2"
        >
          <Icon className="text-sage-600">event</Icon>
          Start: {coach.coachProfile.startDate}
        </Typography>
      )}
      {showAdminDetails && !!coach.coachProfile?.terminationDate && (
        <Typography
          className="flex items-center gap-2 text-sage-600"
          variant="subtitle2"
        >
          <Icon className="text-sage-600">event_busy</Icon>
          Termination: {coach.coachProfile.terminationDate}
        </Typography>
      )}
      {showAdminDetails && !!seatRateEntries.length && (
        <div className="flex flex-col gap-1">
          <Typography
            className="flex items-center gap-2 text-sage-600"
            variant="subtitle2"
          >
            <Icon className="text-sage-600">payments</Icon>
            Rates
          </Typography>
          {seatRateEntries.map(([seat, rate]) => (
            <Typography
              key={`coach-seat-rate-${seat}`}
              className="ml-6 flex items-center gap-2 text-sage-600"
              variant="subtitle2"
            >
              <Icon className="text-sage-600">chevron_right</Icon>
              {formatSeatLabel(seat)}: {rate}
            </Typography>
          ))}
        </div>
      )}
    </section>
  );
};

export default CoachDetails;
